'use client';

import { motion } from 'framer-motion';
import { PlayCircle } from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';

const ARABIC_INDIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

/**
 * Localized "N min" label. The numeric VALUE is identical across locales; only
 * the digit glyphs and the unit noun change.
 */
function formatMinutes(minutes: number, locale: string): string {
  if (locale !== 'ar') return `${minutes} min`;
  const digits = String(minutes).replace(
    /\d/g,
    (d) => ARABIC_INDIC_DIGITS[Number(d)],
  );
  // Arabic plural: 3-10 takes the broken plural, everything else the singular.
  const unit = minutes >= 3 && minutes <= 10 ? 'دقائق' : 'دقيقة';
  return `${digits} ${unit}`;
}

export function FeaturedLessons() {
  const { t, locale, isRTL } = useTranslation();

  const LESSONS = [
    {
      id: 1,
      title: t.academy['Setting up your first AI Agent'],
      category: t.academy['Getting Started'],
      minutes: 12,
    },
    {
      id: 2,
      title: t.academy['Understanding Intent Classification'],
      category: t.academy['AI Concepts'],
      minutes: 18,
    },
    {
      id: 3,
      title: t.academy['Connecting to Zendesk API'],
      category: t.academy.Integrations,
      minutes: 15,
    },
    {
      id: 4,
      title: t.academy['Optimizing Token Usage'],
      category: t.academy.Performance,
      minutes: 10,
    },
    {
      id: 5,
      title: t.academy['Designing Conversation Flows'],
      category: t.academy.Design,
      minutes: 22,
    },
  ];

  return (
    <section className="bg-muted/30 border-y py-24">
      <div className="container">
        <div className="mb-12 flex flex-col items-center justify-between gap-4 md:flex-row">
          <div
            className={`space-y-2 text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}
          >
            <h2 className="font-serif text-3xl sm:text-4xl">
              {t.academy['Latest Lessons']}
            </h2>
            <p className="text-muted-foreground text-lg">
              {
                t.academy[
                  'Bite-sized knowledge to level up your skills quickly.'
                ]
              }
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {LESSONS.map((lesson, index) => (
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              className="group bg-background hover:border-primary/50 flex items-center justify-between rounded-xl border p-4 transition-all hover:shadow-md"
            >
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors">
                  <PlayCircle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-foreground group-hover:text-primary transition-colors">
                    {lesson.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {lesson.category} • {formatMinutes(lesson.minutes, locale)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
